<template>
  <div class="bg-white mt-5 rounded p-3 px-5">
    <div class="w-full flex flex-col md:flex-row border-b py-5">
      <!-- start of search / stats header -->

      <div
        class="w-32 h-32 p-4 -mt-20 ml-2 mb-2 text-white rounded-lg relative flex-no-shrink mr-5" :class="page.icon_color ? page.icon_color : 'bg-tertiary'"
      >
        <icon name="videoettes"></icon>
      </div>

      <div class="w-full flex justify-between">
        <h1 class="text-xl self-end mb-3 uppercase font-medium">My Videoettes</h1>

        <div class="max-w-md flex items-center h-full">
          <div class="text-center border-r px-6">
            <p class="mb-2 text-xxs uppercase font-bold">Total active properties</p>
            <span class="text-xl">{{ stats.property_count }}</span>
          </div>

          <div class="text-center border-r px-6">
            <p class="mb-2 text-xxs uppercase font-bold">Cancelled</p>
            <span class="text-xl text-red">{{ stats.excluded_count }}</span>
          </div>

          <div class="text-center border-r px-6">
            <p class="mb-2 text-xxs uppercase font-bold">Rejected</p>
            <span class="text-xl text-red">{{ stats.rejected_count }}</span>
          </div>

          <div class="text-center px-6">
            <p class="mb-2 text-xxs uppercase font-bold">With Videoettes</p>
            <span class="text-xl text-green">{{ stats.active_count }}</span>
          </div>
        </div>
      </div>
    </div>
    <!-- end of search / stats header -->

    <div class="flex flex-col md:flex-row mb-3 border-b py-5">
      <!-- start of filters -->
      <div
        class="bg-white md:w-1/4 w-full h-full mb-6 md:mb-0 px-3 flex items-center self-end border rounded overflow-hidden md:mr-5 shadow"
      >
        <i class="fa fa-search text-grey"></i>
        <input
          v-model="search"
          type="text"
          class="py-4 px-2 w-full h-full bg-transparent"
          placeholder="Search"
        />
      </div>

      <div class="flex w-full md:w-1/4 mb-8 md:mb-0 md:border-r md:pr-10">
        <div class="flex w-full flex-col lg:mr-3">
          <label for="include" class="mb-2 text-xs">Status:</label>
          <pl-select
            v-model="status"
            name="filter_menu"
            id="include"
            select-class="bg-white rounded h-10 py-0 shadow text-sm"
            class="lg:w-48 w-full"
          >
            <option :value="null">All</option>
            <option value="queued">Queued</option>
            <option value="complete">Publishing</option>
            <option value="published">Published</option>
            <option value="excluded">Cancelled</option>
            <option value="rejected">Rejected</option>
            <option value="failed">Failed</option>
          </pl-select>
        </div>
      </div>
      <div class="flex items-center justify-center md:pl-10">
        <div class="mr-6">
          <input id="sales_filter" type="checkbox" class="blue" v-model="sales" />
          <label for="sales_filter">Sales</label>
        </div>
        <div>
          <input id="lettings_filter" type="checkbox" class="blue" v-model="lettings" />
          <label for="lettings_filter">Lettings</label>
        </div>
      </div>
    </div>
    <!-- end of filters -->

    <div class="w-full flex justify-around text-sm md:hidden">
      <i class="fa fa-arrow-left"></i> Scroll the table left &amp; right to see more
      <i class="fa fa-arrow-right"></i>
    </div>

    <div class="w-full p-3 overflow-x-scroll">
      <table class="w-full min-w-288">
        <thead>
          <tr class="text-xs uppercase">
            <th class="p-3 text-left">Property</th>
            <th class="p-3 w-32">Price</th>
            <th class="p-3 w-32">Department</th>
            <th class="p-3 w-32">Created</th>
            <th class="p-3">Status</th>
            <th class="p-3">View</th>
            <th class="p-3">Social</th>
            <th class="p-3">Download</th>
            <th class="p-3 w-48">Generate</th>
            <th class="p-3">Cancel</th>
          </tr>
        </thead>

        <tbody v-if="videoettes.length > 0">
          <tr
            v-for="(videoette, index) in videoettes"
            :key="videoette.id"
            class="text-xs md:text-sm border-b text-center"
            :class="{
              'bg-grey-lightest': index % 2 === 0
            }"
          >
            <td
              class="p-3 font-bold w-128 border-l-2"
              :class="{
                'border-green': videoette.status === 'Published',
                'border-orange': videoette.status === 'Queued',
                'border-red-light': videoette.status === 'Rejected' || videoette.status === 'Failed',
                'border-blue': videoette.status === 'Cleaned'
              }"
            >
              <div class="flex items-center text-left">
                <div
                  v-if="videoette.image"
                  class="w-16 h-10 bg-cover inline-block mr-2"
                  :style="{ 'backgroundImage': `url(${videoette.image})` }"
                ></div>
                <div class="flex flex-col">
                  <!-- <span class="font-normal text-xs mb-1" :title="videoette.display_address">{{ truncate(videoette.display_address, 24) }}</span> -->
                  <span
                    class="font-normal text-xs mb-1"
                    :title="videoette.display_address"
                  >{{ videoette.display_address }}</span>
                  <span
                    class="font-normal text-xs text-grey"
                  >{{ videoette.town }} {{ videoette.postcode }}</span>
                </div>
              </div>
            </td>
            <td class="p-3 font-bold">{{ videoette.price }}</td>
            <td class="p-3">{{ videoette.department }}</td>
            <td class="p-3 text-xs">
              <template v-if="created = formatCreatedAt(videoette.created_at)">
                <span class="block">{{ created.date }}</span>
                <span class="block mt-1">{{ created.time }}</span>
              </template>
              <span v-else class="block">-</span>
            </td>
            <td
              class="p-3 text-xs"
              :class="{
                'text-green': videoette.status === 'Published',
                'text-orange': videoette.status === 'Queued',
                'text-red-light': videoette.status === 'Rejected' || videoette.status === 'Failed',
                'text-blue': videoette.status === 'Cleaned'
              }"
            >
            {{ videoette.status }}
              <button @click="viewError(videoette)" v-if="videoette.status === 'Rejected' || videoette.status === 'Failed'"
                class="bg-white rounded px-8 border text-xs border-grey-light whitespace-nowrap"
              >View Error</button>
            </td>
            <td class="p-3 w-24">
              <button @click="viewVideoette(videoette)" :disabled="!videoette.rendered">
                <icon name="video" class="w-10 center-x" :center="false" />
              </button>
            </td>
            <td class="p-3 w-24">
              <button
                :disabled="!videoette.rendered"
                :class="{ 'cursor-not-allowed': !videoette.rendered }"
                @click="shareVideoette(videoette)"
              >
                <icon name="social" class="w-10 center-x" :center="false" />
              </button>
            </td>
            <td class="p-3 w-24">
              <button
                :disabled="!videoette.rendered || !videoette.downloadable"
                :class="{ 'cursor-not-allowed': !videoette.rendered }"
                @click="download(videoette)"
              >
                <icon name="download-alt" class="w-10 center-x" :center="false" />
              </button>
            </td>
            <td class="p-3">
              <button
                @click="generate(videoette)"
                class="bg-white rounded px-4 py-2 border text-xs"
                :class="[ canRegenerate(videoette) ? 'border-green' : 'border-grey-light' ]"
                :disabled="!canRegenerate(videoette)"
              >Re-generate</button>
            </td>
            <td
              class="p-3 border-r-2 align-middle"
              :class="{
                'border-green': videoette.status === 'Published',
                'border-orange': videoette.status === 'Queued',
                'border-red-light': videoette.status === 'Rejected' || videoette.status === 'Failed',
                'border-blue': videoette.status === 'Cleaned'
              }"
            >
              <button
                @click="excludeVideoette = videoette"
                class="fa fa-ban fa-fw w-10 center-x"
                :center="false"
                v-show="videoette.status === 'Queued'"
              ></button>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!loading">
          <tr>
            <td colspan="10" class="text-center pt-8 text-2xl text-grey-dark">No Videoettes Found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <view-video-modal
      :show="showVideoetteViewModal"
      :video="selectedVideoette"
      @close="
        showVideoetteViewModal = false;
        selectedVideoette = null;
      "
    />

    <modal v-if="showVideoetteShareModal" @leave="showVideoetteShareModal = null">
      <videoshare :videoId="selectedVideoette.video_id"></videoshare>
    </modal>
    <modal
      v-if="showModalNotAvailable"
      @leave="showModalNotAvailable = false"
    >
      <button
        type="button"
        @click="showModalNotAvailable = false"
        class="flex justify-end items-center"
      >
        <i class="fas fa-times" />
      </button>
      <p>Not Available at the moment</p>
    </modal>

    <modal
      v-if="showErrorModal"
      @leave="showErrorModal = false; selectedVideoette = null"
    >
      <button
        type="button"
        @click="showErrorModal = false; selectedVideoette = null"
        class="flex justify-end items-center"
      >
        <i class="fas fa-times" />
      </button>
      <p class="text-center">{{ selectedVideoette.reason }}</p>
    </modal>

    <div v-if="currentPage < lastPage" class="w-full text-sm flex py-3 justify-center">
      <loader v-if="loading"></loader>
      <button v-else @click="moreResults" class="border-b text-grey-dark">
        SHOW MORE
        <i class="fa fa-caret-down text-xs"></i>
      </button>
    </div>

    <exclude
      :videoette="excludeVideoette"
      @leave="excludeVideoette = null"
    />

    <videoette-download-modal
      :show="showVideoetteDownloadModal"
      :video="selectedVideoette"
      @close="
        showVideoetteDownloadModal = false;
        selectedVideoette = null;
      "
    />
  </div>
</template>

<script>
import api from '@/http'
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { moneyFormat, truncate } from '@/helpers'
import moment from 'moment'
import Videoshare from '@/components/VideoShare'
import axios from 'axios'
import Exclude from '@/components/Videoettes/Exclude'
import VideoetteDownloadModal from '@/product-modules/videoettes/VideoetteDownloadModal'
import ViewVideoModal from '@/product-modules/videoettes/ViewVideoModal'

export default {
  name: 'videoettes',

  props: ['page'],

  components: {
    Videoshare, Exclude, VideoetteDownloadModal, ViewVideoModal
  },

  data () {
    return {
      loading: true,
      req: null,
      currentPage: 1,
      lastPage: 2,
      videoettes: [],
      stats: {
        property_count: 0,
        excluded_count: 0,
        rejected_count: 0,
        active_count: 0
      },
      sales: true,
      lettings: true,
      status: null,
      search: '',
      selectedVideoette: null,
      showVideoetteViewModal: false,
      showVideoetteShareModal: false,
      showModalNotAvailable: false,
      showVideoetteDownloadModal: false,
      showErrorModal: false,
      viewVideo: false,
      excludeVideoette: null
    }
  },

  computed: {
    ...mapState(['selectedOffice'])
  },

  methods: {
    ...mapActions(['displayToastNotification']),

    canRegenerate (videoette) {
      return !videoette.regenerating && (videoette.rendered || (videoette.status === 'Rejected' || videoette.status === 'Cleaned' || videoette.status === 'Failed'))
    },

    generate (videoette) {
      // set this now to prevent multiple re-generate requests
      this.$set(videoette, 'regenerating', true)

      api
        .post(`/videoettes/${videoette.id}`)
        .then(() => {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Regeneration Triggered',
            info: "Your videoette is being regenerated, we'll notify you when it's ready"
          })

          this.currentPage = 1
          this.fetchVideoettes()
        })
        .catch(() => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Regeneration Failed',
            info: 'The chosen videoette cannot be regenerated at this time'
          })
        })
        .finally(() => {
          this.$set(videoette, 'regenerating', false)
        })
    },

    fetchVideoettes: debounce(function () {
      this.loading = true
      if (this.req) {
        this.req.cancel()
      }

      this.req = axios.CancelToken.source()
      api
        .get('/videoettes', {
          cancelToken: this.req.token,
          params: {
            office_id: this.selectedOffice.id,
            page: this.currentPage,
            sales: this.sales,
            lettings: this.lettings,
            status: this.status,
            q: this.search
          }
        })
        .then(response => {
          if (this.currentPage === 1) {
            this.videoettes = response.data.data
          } else {
            this.videoettes = this.videoettes.concat(response.data.data)
          }
          this.lastPage = response.data.meta.last_page
          this.stats = response.data.meta.stats
        })
        .finally(() => {
          this.loading = false
        })
    }, 500),

    moreResults () {
      this.currentPage++
      this.fetchVideoettes()
    },

    download (videoette) {
      this.setSelectedVideoette(videoette)
      this.showVideoetteDownloadModal = true
    },

    getVimeoUrl (vimeoId) {
      if (!vimeoId) return null
      return `https://player.vimeo.com/video/${vimeoId}`
    },

    viewVideoette (videoette) {
      this.setSelectedVideoette(videoette)
      this.showVideoetteViewModal = true
    },

    shareVideoette (videoette) {
      this.setSelectedVideoette(videoette)
      this.showVideoetteShareModal = true
    },

    setSelectedVideoette (videoette) {
      this.selectedVideoette = videoette
    },
    viewError (videoette) {
      this.showErrorModal = true
      this.selectedVideoette = videoette
    },
    notAvailable () {
      this.showModalNotAvailable = true
    },

    formatCreatedAt (timestamp) {
      if (!timestamp) {
        return false
      }

      let localTime = moment.unix(timestamp).local()

      return {
        date: localTime.format('Do MMMM YYYY'),
        time: localTime.format('HH:mm')
      }
    },

    moneyFormat,
    truncate
  },

  created () {
    this.fetchVideoettes()
  },

  watch: {
    showVideoetteViewModal (val) {
      if (val === false) {
        this.selectedVideoette(null)
      }
    },

    showVideoetteShareModal (val) {
      if (val === false) {
        this.selectedVideoette(null)
      }
    },

    selectedOffice () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    sales () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    lettings () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    status () {
      this.currentPage = 1
      this.fetchVideoettes()
    },

    search () {
      this.currentPage = 1
      this.fetchVideoettes()
    }
  }
}
</script>

<style lang="sass">
</style>
